import { type ComputedRef, ref, watch } from 'vue'

import { normalizeUserTag } from '@/lib/adapters/user-tags.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error.stores'
import type { UserTag, UserTagServer } from '@/types/userTags'

export const useGetUserTags = (advertiserIdRef: ComputedRef<number | undefined>) => {
  const data = ref<UserTag[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchUserTags = async (advertiserId: number) => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.get<UserTagServer[]>(`/stores/${advertiserId}/user-tags`)
      data.value = response.data.map(normalizeUserTag)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(
    advertiserIdRef,
    async () => {
      const advertiserId = advertiserIdRef.value
      if (advertiserId) {
        await fetchUserTags(advertiserId)
      }
    },
    { immediate: true },
  )

  return { data, isLoading, error }
}

export const useSetUserTagsOnUser = (advertiserIdRef: ComputedRef<number | undefined>) =>
  useApi({
    setUserTagsOnUser: async (userId: number, tags: { id?: number; name: string }[]) => {
      const advertiserId = advertiserIdRef.value
      if (!advertiserId) {
        return []
      }
      const response = await request.post<UserTagServer[]>(
        `/stores/${advertiserId}/user/${userId}/user-tags`,
        { tags },
      )
      return response.data.map(normalizeUserTag)
    },
  })
